import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, getAdminToken } from "../../lib/api";

const POSTER_NEUTRAL =
  "https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=1600&q=85";

const CANDIDATES = [
  // ====================  STUDIO EDITORIAL  ====================
  {
    id: "52278", group: "Studio Editorial",
    label: "White Dress, Red Boots — Poses",
    description: "Beautiful model in a white dress and bold red boots, modeling poses on a pure white backdrop.",
    url_1080: "https://assets.mixkit.co/videos/52278/52278-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52278/52278-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52278/52278-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "52279", group: "Studio Editorial",
    label: "Red Boots — Movement",
    description: "Vertical — white dress and shiny red boots over a clean white backdrop.",
    url_1080: "https://assets.mixkit.co/videos/52279/52279-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52279/52279-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52279/52279-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "52281", group: "Studio Editorial",
    label: "White Dress + Red Accents",
    description: "Vertical close-up — model touches her face, white dress with red details.",
    url_1080: "https://assets.mixkit.co/videos/52281/52281-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52281/52281-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52281/52281-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "50640", group: "Studio Editorial",
    label: "Creative Studio — Model & Photographer",
    description: "Model poses under the lights of a fashion-studio photoshoot while a photographer works.",
    url_1080: "https://assets.mixkit.co/videos/50640/50640-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/50640/50640-720.mp4",
    thumb: "https://assets.mixkit.co/videos/50640/50640-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "50641", group: "Studio Editorial",
    label: "Studio — Model Turns to Pose",
    description: "Model turns to camera as a photographer takes photos in a creative studio.",
    url_1080: "https://assets.mixkit.co/videos/50641/50641-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/50641/50641-720.mp4",
    thumb: "https://assets.mixkit.co/videos/50641/50641-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "50639", group: "Studio Editorial",
    label: "Studio — Editorial Behind the Scenes",
    description: "Wide shot of an editorial studio setup with lights, photographer and a model in frame.",
    url_1080: "https://assets.mixkit.co/videos/50639/50639-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/50639/50639-720.mp4",
    thumb: "https://assets.mixkit.co/videos/50639/50639-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },

  // ====================  VEIL / RED BACKDROP  ====================
  {
    id: "52277", group: "Veil · Red Backdrop",
    label: "Veil Portrait — Intense Gaze",
    description: "Vertical — young woman in a white veil, eyes locked on the lens.",
    url_1080: "https://assets.mixkit.co/videos/52277/52277-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52277/52277-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52277/52277-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "52274", group: "Veil · Red Backdrop",
    label: "Veil — White Attire, Red Background",
    description: "Vertical — white attire and a delicate veil set against a bold red backdrop.",
    url_1080: "https://assets.mixkit.co/videos/52274/52274-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52274/52274-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52274/52274-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "52275", group: "Veil · Red Backdrop",
    label: "Veil — Side Gaze",
    description: "Model in all-white attire moves her veil to the side, eyes on the camera.",
    url_1080: "https://assets.mixkit.co/videos/52275/52275-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52275/52275-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52275/52275-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "52276", group: "Veil · Red Backdrop",
    label: "Veil — Smile, Red Backdrop",
    description: "Vertical — model in a veil smiles and poses against a bold red backdrop.",
    url_1080: "https://assets.mixkit.co/videos/52276/52276-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52276/52276-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52276/52276-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "52272", group: "Veil · Red Backdrop",
    label: "White Outfit · Red Backdrop",
    description: "Beautiful model in a white outfit posing against a vivid red background.",
    url_1080: "https://assets.mixkit.co/videos/52272/52272-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52272/52272-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52272/52272-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },

  // ====================  BEAUTY CLOSE-UPS  ====================
  {
    id: "52041", group: "Beauty",
    label: "Beauty — Lip Retouch Close-up",
    description: "Makeup artist finishes the lip with a fine brush. Beauty editorial close-up.",
    url_1080: "https://assets.mixkit.co/videos/52041/52041-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52041/52041-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52041/52041-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "52055", group: "Beauty",
    label: "Beauty — Eyelid Makeup",
    description: "Closed-eye close-up — delicate eyelid makeup in a softly-lit studio.",
    url_1080: "https://assets.mixkit.co/videos/52055/52055-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52055/52055-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52055/52055-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "52040", group: "Beauty",
    label: "Beauty — Powder Application",
    description: "Soft brush applies powder to a model's cheek. Editorial beauty texture.",
    url_1080: "https://assets.mixkit.co/videos/52040/52040-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52040/52040-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52040/52040-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "52042", group: "Beauty",
    label: "Beauty — Eyeliner Detail",
    description: "Precise eyeliner application in extreme close-up.",
    url_1080: "https://assets.mixkit.co/videos/52042/52042-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52042/52042-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52042/52042-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "52050", group: "Beauty",
    label: "Beauty — Lashes Close",
    description: "Eyelash retouch close-up. Soft studio key light.",
    url_1080: "https://assets.mixkit.co/videos/52050/52050-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/52050/52050-720.mp4",
    thumb: "https://assets.mixkit.co/videos/52050/52050-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },

  // ====================  CONCEPTUAL · MOTION · LIGHT  ====================
  {
    id: "51291", group: "Conceptual",
    label: "Portrait · Roka Light",
    description: "Direct portrait of a young woman, dynamic colored backdrop.",
    url_1080: "https://assets.mixkit.co/videos/51291/51291-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/51291/51291-720.mp4",
    thumb: "https://assets.mixkit.co/videos/51291/51291-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "51292", group: "Conceptual",
    label: "Captivating — Dancing Into the Lens",
    description: "Vertical portrait — captivating dancer doing a colourful performance into camera.",
    url_1080: "https://assets.mixkit.co/videos/51292/51292-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/51292/51292-720.mp4",
    thumb: "https://assets.mixkit.co/videos/51292/51292-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "51277", group: "Conceptual",
    label: "Colourful Dance — Blue Light",
    description: "Talented dancer under blue light, waving backdrop of changing colours.",
    url_1080: "https://assets.mixkit.co/videos/51277/51277-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/51277/51277-720.mp4",
    thumb: "https://assets.mixkit.co/videos/51277/51277-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "51286", group: "Conceptual",
    label: "Pink Light — Vertical Performance",
    description: "Dancer in pink performing against a colourful background. Vertical, dreamy.",
    url_1080: "https://assets.mixkit.co/videos/51286/51286-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/51286/51286-720.mp4",
    thumb: "https://assets.mixkit.co/videos/51286/51286-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "51275", group: "Conceptual",
    label: "White Attire — Conceptual",
    description: "Young dancer in white, blue lit, with a shifting colour backdrop.",
    url_1080: "https://assets.mixkit.co/videos/51275/51275-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/51275/51275-720.mp4",
    thumb: "https://assets.mixkit.co/videos/51275/51275-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "51280", group: "Conceptual",
    label: "Shadow — Stripes Backdrop",
    description: "Shadow of a woman dancing in front of moving colored stripes.",
    url_1080: "https://assets.mixkit.co/videos/51280/51280-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/51280/51280-720.mp4",
    thumb: "https://assets.mixkit.co/videos/51280/51280-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "51268", group: "Conceptual",
    label: "Green Light — Slow Conceptual",
    description: "Dancer under green light moves slowly against shifting colour.",
    url_1080: "https://assets.mixkit.co/videos/51268/51268-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/51268/51268-720.mp4",
    thumb: "https://assets.mixkit.co/videos/51268/51268-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "51272", group: "Conceptual",
    label: "Red Light — Slow Dance",
    description: "Face illuminated red, slow artistic dance on an abstract colour backdrop.",
    url_1080: "https://assets.mixkit.co/videos/51272/51272-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/51272/51272-720.mp4",
    thumb: "https://assets.mixkit.co/videos/51272/51272-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "51273", group: "Conceptual",
    label: "Portrait — Pink & Blue",
    description: "Portrait of a dancer in slow artistic performance under blue lighting.",
    url_1080: "https://assets.mixkit.co/videos/51273/51273-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/51273/51273-720.mp4",
    thumb: "https://assets.mixkit.co/videos/51273/51273-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "39881", group: "Conceptual",
    label: "Spinning — Studio Motion",
    description: "Young woman spins under dim blue and pink lights. High-fashion motion piece.",
    url_1080: "https://assets.mixkit.co/videos/39881/39881-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/39881/39881-720.mp4",
    thumb: "https://assets.mixkit.co/videos/39881/39881-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },

  // ====================  URBAN · OUTDOOR · CINEMATIC  ====================
  {
    id: "42286", group: "Urban · Outdoor",
    label: "Light Bars — Two Faces",
    description: "Two models between bars of coloured light, looking into the lens.",
    url_1080: "https://assets.mixkit.co/videos/42286/42286-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/42286/42286-720.mp4",
    thumb: "https://assets.mixkit.co/videos/42286/42286-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "42581", group: "Urban · Outdoor",
    label: "Neon Bus — Conceptual",
    description: "Three urban models pose inside a neon-lit bus — moody, conceptual.",
    url_1080: "https://assets.mixkit.co/videos/42581/42581-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/42581/42581-720.mp4",
    thumb: "https://assets.mixkit.co/videos/42581/42581-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "34400", group: "Urban · Outdoor",
    label: "Desert — Captivating Stare",
    description: "Young woman captivating the camera with the desert and mountains behind her.",
    url_1080: "https://assets.mixkit.co/videos/34400/34400-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/34400/34400-720.mp4",
    thumb: "https://assets.mixkit.co/videos/34400/34400-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "34404", group: "Urban · Outdoor",
    label: "Desert — Black Dress",
    description: "Young woman in a black dress posing captivatingly in a vast desert.",
    url_1080: "https://assets.mixkit.co/videos/34404/34404-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/34404/34404-720.mp4",
    thumb: "https://assets.mixkit.co/videos/34404/34404-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "18208", group: "Urban · Outdoor",
    label: "Park — Red Dress, Umbrella",
    description: "Fashion woman in a red dress walking a forest path with an umbrella.",
    url_1080: "https://assets.mixkit.co/videos/18208/18208-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/18208/18208-720.mp4",
    thumb: "https://assets.mixkit.co/videos/18208/18208-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },

  // ====================  CLASSIC  ====================
  {
    id: "805", group: "Classic",
    label: "Black & White Dress — Boutique",
    description: "Woman in a short black-and-white dress modelling inside a clothing store.",
    url_1080: "https://assets.mixkit.co/videos/805/805-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/805/805-720.mp4",
    thumb: "https://assets.mixkit.co/videos/805/805-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
  {
    id: "809", group: "Classic",
    label: "Multiple Mirrors — Current Default",
    description: "Fashion model examines her white dress in multiple mirrors. The original hero.",
    url_1080: "https://assets.mixkit.co/videos/809/809-1080.mp4",
    url_720: "https://assets.mixkit.co/videos/809/809-720.mp4",
    thumb: "https://assets.mixkit.co/videos/809/809-thumb-360-0.jpg",
    poster: POSTER_NEUTRAL,
  },
];

export default function HeroVideoPicker() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!getAdminToken()) { navigate("/admin/login"); return; }
    api.get("/site/settings")
      .then((r) => {
        const u = r.data?.hero_video?.url_1080 || "";
        // Match by mixkit video ID inside the URL, e.g. /videos/52278/52278-1080.mp4
        const m = u.match(/videos\/(\d+)\//);
        setCurrent(m ? m[1] : null);
      })
      .catch(() => {});
  }, [navigate]);

  const pick = async (c) => {
    setSaving(c.id);
    try {
      await api.put("/admin/site/hero-video", {
        url_1080: c.url_1080,
        url_720: c.url_720,
        poster: c.poster,
        label: c.label,
      });
      setCurrent(c.id);
      toast.success(`Hero set to "${c.label}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally { setSaving(null); }
  };

  return (
    <div data-testid="hero-picker-page" className="min-h-screen bg-white text-[#0A0A0A] pt-24">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-24">
        <Link to="/admin" data-testid="hero-picker-back" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-black">
          <ChevronLeft size={14}/> Back to dashboard
        </Link>
        <div className="mt-6 flex items-end justify-between flex-wrap gap-6">
          <div>
            <p className="label-overline text-zinc-500">Site settings · 01</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light mt-3 leading-none">
              Hero video.
            </h1>
            <p className="text-zinc-600 mt-3 text-sm max-w-2xl">
              Pick the looping video shown behind the headline on the homepage.
              Each option autoplays on hover. Your choice goes live instantly — no redeploy required.
            </p>
          </div>
          <Link to="/" target="_blank" className="btn-ghost">Preview homepage ↗</Link>
        </div>

        {(() => {
          const groups = [...new Set(CANDIDATES.map((c) => c.group || "Other"))];
          return groups.map((g) => {
            const items = CANDIDATES.filter((c) => (c.group || "Other") === g);
            return (
              <section key={g} data-testid={`hero-group-${g.toLowerCase().replace(/\s+/g, '-')}`} className="mt-16 first:mt-12">
                <div className="flex items-baseline justify-between mb-6 border-b border-black/10 pb-3">
                  <h2 className="font-serif text-2xl tracking-tight">{g}</h2>
                  <p className="label-overline text-zinc-500">{items.length} clip{items.length === 1 ? "" : "s"}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map((c) => {
                    const isActive = current === c.id;
                    const isSaving = saving === c.id;
                    return (
                      <div
                        key={c.id}
                        data-testid={`hero-candidate-${c.id}`}
                        className={`group border ${isActive ? "border-black ring-2 ring-black" : "border-black/10 hover:border-black/40"} bg-white transition-all`}
                      >
                        <div
                          className="relative aspect-video bg-black overflow-hidden"
                          onMouseEnter={(e) => { const v = e.currentTarget.querySelector("video"); if (v) v.play().catch(() => {}); }}
                          onMouseLeave={(e) => { const v = e.currentTarget.querySelector("video"); if (v) v.pause(); }}
                        >
                          <img src={c.thumb} alt={c.label} className="absolute inset-0 w-full h-full object-cover group-hover:opacity-0 transition-opacity"/>
                          <video
                            muted
                            loop
                            playsInline
                            preload="none"
                            poster={c.poster}
                            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <source src={c.url_720} type="video/mp4"/>
                          </video>
                          {isActive && (
                            <span className="absolute top-3 right-3 bg-white text-black text-[10px] uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1 z-10">
                              <CheckCircle2 size={11}/> Live
                            </span>
                          )}
                          <span className="absolute top-3 left-3 bg-black/80 text-white text-[10px] uppercase tracking-wider px-2 py-1 font-mono z-10">
                            #{c.id}
                          </span>
                        </div>
                        <div className="p-5">
                          <p className="font-serif text-lg leading-tight">{c.label}</p>
                          <p className="text-xs text-zinc-500 mt-2 leading-relaxed min-h-[2.5rem]">{c.description}</p>
                          <button
                            type="button"
                            data-testid={`hero-use-${c.id}`}
                            disabled={isActive || isSaving}
                            onClick={() => pick(c)}
                            className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${
                              isActive
                                ? "bg-black text-white cursor-default"
                                : "bg-white text-black border border-black hover:bg-black hover:text-white"
                            }`}
                          >
                            {isSaving
                              ? (<><Loader2 size={12} className="animate-spin"/> Saving…</>)
                              : isActive
                              ? "Currently live"
                              : "Set as homepage hero"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          });
        })()}

        <p className="text-xs text-zinc-500 mt-10 max-w-2xl">
          Videos are sourced from Mixkit's free editorial library (commercial use allowed, no watermark).
          The selected video is loaded on every homepage visit.
        </p>
      </div>
    </div>
  );
}
